<template>
  <div class="chat-conversation" :class="{active: isShowChatBody, 'mini': miniChat}" @click="handleDocumentClick(e)">
    <div class="chat-body" :class="{'mini': miniChat}">
      <ChatBodyHead v-if="currentUser || isActiveConversiation"
        :currentUser="currentUser"
        :currentUserUuid="currentUserUuid"
        :isSeller="isSeller"
        :isMobile="isMobile"
        :isTablet="isTablet"
        :isDesktop="isDesktop"
        :supportName="supportName"
        :miniChat="miniChat"
        :supportImg="supportImg"
        :testUrlQuickChat="testUrlQuickChat"
        :isActiveUserInfo="isActiveUserInfo"
        :isActiveSearchMsg="isActiveSearchMsg"
        :isActiveSearchResult="isActiveSearchResult"
        :isMiniChatLoad="isMiniChatLoad"
        :isHideUserList="isHideUserList"
        :isLoadingMessages="isLoadingMessages"
        :isEnableInfo="isEnableInfo || !!(allChatImages && allChatImages.length) || !!(allChatFiles && allChatFiles.length)"
        @back="btnBack"
        @active-search-result-head="isActiveSearchResult = $event"
        @toggle-info-head="toggleUserInfo"
        @toggle-search-head="toggleSearch"
        @message-find="openFindMessage"
        @user-mute="({ userId, isMute }) => userMute(userId, isMute)"
      />
      <div v-else-if="!miniChat" class="chat-body__empty">
        <svg v-once class="wc-i" aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z"/><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"/></svg>
        <template v-if="hasNoConversations">
          <span>{{ $t("No conversations yet") }}</span>
          <p>{{ $t("When you message a seller or buyer, the conversation will appear here.") }}</p>
        </template>
        <template v-else>
          <span>{{ $t("Select a conversation") }}</span>
          <p>{{ $t("Choose a chat on the left to see messages, order updates and shared files.") }}</p>
        </template>
      </div>
      <div class="chat-body__wrap"
        @touchstart="handleTouchStart($event)"
        @touchend="handleTouchEnd($event)"
        :class="{'mini': miniChat, 'dragging': isDraggingFiles && !updateMessageData, 'search-active': isActiveSearchResult && isActiveSearchMsg}">
        <!-- Drop zone while files are dragged over the thread (design D-Drag) -->
        <div class="chat-drop" v-if="isDraggingFiles && !updateMessageData" aria-hidden="true">
          <span class="chat-drop__icon"><svg class="wc-i" aria-hidden="true" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg></span>
          <span class="chat-drop__title">{{ $t("Drop files to attach") }}</span>
          <span class="chat-drop__hint">{{ $t("Up to {count} files, {mb} MB each", { count: 5, mb: 5 }) }} · JPG, PNG, PDF, TXT, ZIP, RAR, 7Z</span>
        </div>
        <button class="chat-wrap__btn-to-bottom" :class="{'active-info': isActiveUserInfo && !isDesktop}" v-if="isShowScrollBtn && (!miniChat || !ifMiniDesktop) && !isSkeletonMsgOn && !isNoMessagesYet" @click="scrollToBottomBtn">
          <svg class="wc-i" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
        </button>
        <div v-if="isSkeletonMsgOn" class="chat-wrap skeleton-wrap">
          <!-- Loading thread (design S1): placeholder bubbles in the 760px column -->
          <div class="msg-skeleton" aria-hidden="true">
            <div class="msg-skeleton__row" v-for="(row, index) in skeletonRows" :key="index" :class="row.out ? 'is-out' : 'is-in'">
              <span class="msg-skeleton__avatar" v-if="!row.out && !miniChat && !isMobile"></span>
              <span class="msg-skeleton__bubble" :style="{ width: row.w, height: row.h }"></span>
            </div>
          </div>
        </div>
        <div v-else-if="isNoMessagesYet"
          class="chat-body__empty">
            <svg v-once class="wc-i" aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z"/><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"/></svg>
            <span>{{ $t("No messages yet") }}</span>
        </div>
        <div v-else class="chat-wrap" ref="chatContainer" 
          :class="{'hide-scroll': openTooltipMessageId || isActiveUserBG, 'mini': miniChat, 'edit-reply-msg': isEditReplyMsg, 'padding-top': !isActiveSearchMsg && !isActiveSearchResult}">
          <div v-for="group in messageList"
            :key="group.date" class="message-group"
            :class="{'active-info' : isActiveUserInfo && !isDesktop && !miniChat, 'one-system': messageList.length === 1 && group.messages.length === 1 && group.messages[0].type !== 1}">
            <div class="message-date" @click="handleDateHeaderClick">
              <div class="message-date__block">
                {{ group.date }}
              </div>
            </div>
            <div class="message" v-for="msg in group.messages" :key="msg.timemark ? msg.timemark : msg.id" 
              :data-id="msg?.id"
              :data-timemark="msg?.timemark"
              :class="[{
                'sender': msg.user_id !== myUser.id,
                'author': msg.user_id === myUser.id,
                'active': searchMessageId === msg.id && !openTooltipMessageId,
                'support': msg.type !== 1,
                'hover': openTooltipMessageId === msg.id,
                'just-sent': msg.just_sent,
                'first': isFirstInGroup(msg)
              }]">
              <ChatMsgSupport v-if="msg.type !== 1"
                :currentUserUuidProp="currentUserUuid"
                :msg="msg"
                :mobile="miniChat || isMobile"/>
              <ChatGroupMsg v-else
                :msg="msg"
                :isFirstInGroup="isFirstInGroup(msg)"
                :supportImg="supportImg"
                :currentUserUuidProp="currentUserUuid"
                :miniChat="miniChat"
                :currentUser="currentUser"
                :isMobile="isMobile"
                :isTablet="isTablet"
                :isTooltipActive="openTooltipMessageId === msg.id"
                :isVisible="visibleImgMessages.has(String(msg.id))"
                @open-file="openFileMsgInfo"
                @open-img="openImgMsgInfoOpen"
                @copy-message="copyMessage"
                @toggle-react="reactionsEmoji"
                @toggle-tooltip="onToggleTooltip"
                @open-replied-message="openFindMessage"
                @update-message="openUpdateMessage"
                @reply-message="replyMsg"/>
            </div>
          </div>
        </div>
      </div>
      <div class="chat-body__bottom" :class="{'active-back': isEditReplyMsg || isActiveTemplates, 'active-info' : isActiveUserInfo && !isDesktop && !miniChat}" v-if="currentUser || isActiveConversiation">
        <ChatTemplates
          :miniChat="miniChat"
          :isMobile="isMobile"
          :isActiveTemplates="isActiveTemplates"
          @chose-template="choseTemplate"
          @open-templates="userTemplate"/>
        <SlideUpDown :isActive="isEditReplyMsg">
          <div class="chat-body__replyEdit">
            <svg v-if="updateMessageData" class="wc-i" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/></svg>
            <svg v-else class="wc-i" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
            <div class="chat-body__replyEdit-wrap">
              <div class="chat-body__replyEdit-msg">
                <div class="chat-body__replyEdit-msg-user">
                  {{ updateMessageData ? $t("Edit message") :
                    replyEditMsgData?.user_id === myUser?.id ? $t("You") : currentUser?.user.username
                  }}
                </div>
                <div class="chat-body__replyEdit-msg-text" v-html="updateMessageData && replyEditMsgData?.files?.length ? escapeHtml(filesSummary(replyEditMsgData.files)) : replyEditMsgData?.text ? sanitizeHtml(replyEditMsgData.text) : fileOrImage(replyEditMsgData?.files)"></div>
              </div>
              <svg v-once class="wc-i chat-body__replyEdit-cancel" @click="closeReply" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </div>
          </div>
        </SlideUpDown>
        <div class="chat-body__bottom-wrap">
          <ChatUplaodBottom
            :isActiveBack="isEditReplyMsg || isActiveTemplates"
            :contentMsg="contentMsg"
            :isActiveTemplates="isActiveTemplates"
            :filesProp="draggingFiles"
            :uuidList="currentUserUuid"
            :isTablet="isTablet"
            :testUrlQuickChat="testUrlQuickChat"
            :updateMessageData="updateMessageData"
            :replyMsgId="replyEditMsgData?.id ? replyEditMsgData.id : null"
            @delete-files="deleteFilesFromChat"
            @updateText="updateText"
            @close-edit-reply="closeReply"
            @add-emoji="addEmoji"
          />
          <div class="chat-body__form" :class="{mini: miniChat, 'templates': isActiveTemplates, 'hide-tmp': !isShowBtnTemplate}">
            <transition name="template-btn">
              <div v-if="isShowBtnTemplate" class="chat-body__form-templates-img" :class="{active: isActiveTemplates}" @click="userTemplate()">
                  <svg v-once class="wc-i" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="18" x="4" y="3" rx="2"/><path d="M8 8h8"/><path d="M8 12h8"/><path d="M8 16h5"/></svg>
              </div>
            </transition>
            <div class="chat-body__form-input">
              <div contenteditable 
                class="chat-input"
                ref="chatInput"
                @input="checkPlaceholder"
                @keydown="handleKeyDown"
                @focus="handleFocus"
                @paste="handlePaste"
                v-html="content"></div>
              <span class="chat-input__placeholder" v-if="inputPlaceholder">{{ $t("Write a message...") }}</span>
            </div>
            <ChatFormBtns
              :isActiveBtn="!inputPlaceholder && !isSending"
              :disableBtnSmile="isActiveTemplates && !isSending"
              :contentMsg="contentMsg"
              :isTablet="isTablet"
              :isEdit="!!updateMessageData"
              @add-emoji="addEmoji"
              @send-click="clickBtnSend"/>
          </div>
        </div>
      </div>
    </div>
    <UserInfo
      v-if="currentUser"
      ref="userInfoRef"
      @user-info="toggleUserInfo(false)"
      @open-file="openFileMsgInfo"
      @message-find="openFindMessage"
      @open-img="openImgMsgInfoOpen"
      @user-mute="({ userId, isMute }) => userMute(userId, isMute)"
      @is-enable-info="isEnableInfo = $event"
      :isTablet="isTablet"
      :isMobile="isMobile"
      :language="language"
      :miniChat="miniChat"
      :myUser="myUser"
      :isLoadingMessages="isLoadingMessages"
      :currentUserUuidProp="currentUserUuid"
      :isActiveUserInfo="isActiveUserInfo"
      :currentUser="currentUser"
      :allChatImages="allChatImages"
      :activeTooltipBody="activeTooltipBody"
      :allChatFiles="allChatFiles"/>
    <transition name="fade-info-bg">
      <div class="chat-conversation__info-bg"
        v-if="isActiveUserInfo && isDesktop"
        @click="toggleUserInfo(false)"
      ></div>
    </transition>
    <ModalImagesSlider 
      :mobile="isMobile"
      :showModal="isActiveModalImages" 
      :allChatImages="allChatImages"
      :activeImageIndex="activeImageIndex"
      :isActiveMoodalSidebar="isActiveMoodalSidebar"
      @active-img="openImgMsgInfo"
      @close-modal="closeModalImageSlider"/>
  </div>
</template>

<script>
import { toRaw } from 'vue';
import MuteUser from './MuteUser.vue';
import ReportUser from './ReportUser.vue';
import ChatTemplates from './ChatTemplates.vue';
import ChatUplaodBottom from './ChatUplaodBottom.vue';
import ChatGroupMsg from './ChatGroupMsg.vue';
import ChatMsgSupport from './ChatMsgSupport.vue';
import ChatBodyHead from './ChatBodyHead.vue';
import UserInfo from './ChatUserInfo.vue';
import ChatFormBtns from './ChatFormBtns.vue';
import ModalImagesSlider from './ModalImagesSlider.vue';
import SlideUpDown from '/webpack-assets/vue/templates/chat/parts/ChatSlideUpDown.vue';
import { renderAlertVue } from "/webpack-assets/vue/utils/common.js";
import axios from '/webpack-assets/vue/config/axios-config';
import { chatFetch } from './chatTokenManager';
import { escapeHtml } from '/webpack-assets/vue/utils/escapeHtml.js';
import { sanitizeHtml } from '/webpack-assets/vue/utils/sanitizeHtml.js';

export default {
  components: {
    UserInfo,
    MuteUser,
    ReportUser,
    ChatTemplates,
    ChatGroupMsg,
    ChatMsgSupport,
    SlideUpDown,
    ChatBodyHead,
    ChatFormBtns,
    ChatUplaodBottom,
    ModalImagesSlider
  },
  data() {
    return {
      isActiveUserInfo: false,
      isActiveUserBG: false,
      isSending: false,
      touchStartX: 0,
      touchStartY: 0,
      greetText: [],
      messageList: [],
      firstInGroupIds: new Set(),
      responseMessageList: [],
      cacheBefore: [],
      cacheAfter: [],
      windowSize: 50,
      responseSize: 30,
      isEnableInfo: false,
      skeletonRows: [
        { out: false, w: '46%', h: '44px' },
        { out: true, w: '34%', h: '44px' },
        { out: false, w: '58%', h: '64px' },
        { out: true, w: '24%', h: '44px' },
      ],
      hasMoreBefore: true,
      hasMoreAfter: true,
      searchTimeout: null,
      responseCountMsgBefore: 30,
      responseCountMsgAfter: 30,
      isMiniChatLoad: true,
      localUserUuid: null,
      inputPlaceholder: true,
      isActiveTemplates: false,
      isSeller: window.common.user.is_seller,
      supportImg: '/img/icons/logo-small.svg',
      supportName: 'WYREL',
      isSkeletonMsgOn: false,
      isNoMessagesYet: false,
      isEditReplyMsg: false,
      replyEditMsgData: null,
      isScrollNearTop: true,
      isScrollNearBottom: false,
      firstTimemarkMsg: null,
      lastTimemarkMsg: null,
      oldFirstTimemarkMsg: null,
      lastIdMsgSearch: null,
      oldLastTimemarkMsg: null,
      searchMessageId: null,
      updateMessageData: null,
      isActiveSearchMsg: false,
      isActiveSearchResult: false,
      isShowScrollBtn: false,
      highlightTimeout: null,
      closeReplyTimeout: null,
      openTooltipMessageId: null,
      contentMsg: '',
      isDraggingFiles: false,
      dragCounter: 0,
      draggingFiles: [],
      visibleImgMessages: new Set(),
      observer: null,
      allChatImages: [],
      isActiveModalImages: false,
      isActiveMoodalSidebar: false,
      activeImageIndex: null,
      allChatFiles: [],
      activeTooltipBody: false,
    };
  },
  props: {
    testUrlQuickChat: {
      type: String,
      default: ''
    },
    currentUser: {
      type: Object,
      required: false
    },
    currentUserUuid: {
      type: String,
      required: true
    },
    isHideUserList: {
      type: Boolean,
      default: false,
    },
    // the user has no conversations at all (first list request returned nothing)
    hasNoConversations: {
      type: Boolean,
      default: false,
    },
    isActiveConversiation: {
      type: Boolean,
      default: false,
    },
    myUser: {
      type: Object,
      required: true
    },
    isDesktop: {
      type: Boolean,
      default: false    
    },
    ifMiniDesktop: {
      type: Boolean,
      default: false
    },
    isTablet: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    isShowChatBody: {
      type: Boolean,
      default: true
    },
    language: {
      type: String,
      default: 'en'
    },
    miniChat: {
      type: Boolean,
      default: false
    },
    isLoadingMessages: {
      type: Boolean,
      default: false
    }
  },
  beforeUnmount() {
    const chatContainerEl = this.$refs.chatContainer;
    if (chatContainerEl) {
      chatContainerEl.removeEventListener('scroll', this.handleScroll);
    }
    this.observer?.disconnect();
    this.observer = null;

    if (window.handleReactionsMessage === this.handleReactionsMessage) {
      delete window.handleReactionsMessage;
    }

    this.$el.removeEventListener('dragenter', this.handleDragEnter);
    this.$el.removeEventListener('dragover', this.handleDragOver);
    this.$el.removeEventListener('dragleave', this.handleDragLeave);
    this.$el.removeEventListener('drop', this.handleDrop);

    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout);
      this.highlightTimeout = null;
    }
    if (this.closeReplyTimeout) {
      clearTimeout(this.closeReplyTimeout);
      this.closeReplyTimeout = null;
    }
  },
  async mounted() {
    this.$el.addEventListener('dragenter', this.handleDragEnter);
    this.$el.addEventListener('dragover', this.handleDragOver);
    this.$el.addEventListener('dragleave', this.handleDragLeave);
    this.$el.addEventListener('drop', this.handleDrop);

    window.handleReactionsMessage = this.handleReactionsMessage;
    try {
      if(this.isActiveConversiation) {
        this.isMiniChatLoad = false;
        this.isSkeletonMsgOn = true;
        this.localUserUuid = this.currentUserUuid
        if(this.currentUserUuid) {
          await this.getMessages(this.currentUserUuid);
          const [files, images] = await Promise.all([
            this.getOriginFileInfo(this.currentUserUuid, 'file'),
            this.getOriginFileInfo(this.currentUserUuid, 'images')
          ]);

          this.allChatFiles = files || [];
          this.allChatImages = images || [];
        }
      }
    } catch (e) {
      console.error('Error initializing chat:', e);
    }
  },
  watch: {
    isActiveConversiation(bool){
      if(bool) {
        this.isMiniChatLoad = false;
        this.isSkeletonMsgOn = true;
      }
    },
    async currentUserUuid(val) {      
      const chatInput = this.$refs.chatInput;
      this.isSkeletonMsgOn = true;
      this.isEditReplyMsg = false;

      if(val !== this.localUserUuid) {
        this.responseCountMsgBefore = 30;
        this.responseCountMsgAfter = 30;
        this.isScrollNearTop = true;
        this.isShowScrollBtn = false;
        this.isScrollNearBottom = false;
        this.isActiveTemplates = false;
        this.handleDocumentClick()
        this.toggleSearch(false)
        this.localUserUuid = val;

        await this.getMessages(val);
        const [files, images] = await Promise.all([
          this.getOriginFileInfo(this.currentUserUuid, 'file'),
          this.getOriginFileInfo(this.currentUserUuid, 'images')
        ]);

        this.allChatFiles = files;
        this.allChatImages = images;
        if (chatInput) {
          chatInput.innerText = '';
          this.inputPlaceholder = true
        }
      }
    },
    currentUser: {
      handler(oldVal, newVal) {
        if (newVal?.user?.id === oldVal?.user?.id) return;
        if(this.isActiveConversiation && !this.miniChat) {
          this.isMiniChatLoad = true;
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    content() {
      return this.greetText.map(text => `<span>${text} </span>`).join('');
    },
    isShowBtnTemplate() {
      if(this.isEditReplyMsg || this.isActiveTemplates) {
        if (this.updateMessageData === null && !this.isActiveTemplates) return false;
        return this.inputPlaceholder;
      } else {
        return true;
      };
    }
  },
  methods: {
    escapeHtml,
    sanitizeHtml,
    closeModalImageSlider() {
      this.activeTooltipBody = true;
      this.isActiveModalImages = false;
      setTimeout(() => {
          this.activeTooltipBody = false;
      },1000)
    },
    fileOrImage(files) {
      if (!files || !files.length) return 'unknown';

      const lastFile = files[files.length - 1];
      const name = lastFile.original_name || '';
      const ext = (name.split('.').pop() || '').toLowerCase();
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

      return imageExts.includes(ext) ? this.$t("Image") : this.$t("Document");
    },
    // "2 files · receipt.pdf, shot.png" for the edit bar of a message with attachments
    filesSummary(files) {
      const names = files.map(f => f.original_name).join(', ');
      return `${this.$t('{count} files', { count: files.length })} · ${names}`;
    },
    async getOriginFileInfo(id, param = null) {
      let url;

      if (param === 'file') {
        url = `${this.testUrlQuickChat}message-file/list?conversation_uuid=${id}&type=file`;
      } else if (param === 'images') {
        url = `${this.testUrlQuickChat}message-file/list?conversation_uuid=${id}&type=image`;
      } else {
        url = `${this.testUrlQuickChat}message-file?message_file_id=${id}&version=original`;
      }

      try {
        const response = await chatFetch(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          console.warn('HTTP error:', response.status, url);
          return null;
        }

        if (param === null) {
          const blob = await response.blob();
          return await this.blobToBase64(blob);
        }

        const result = await response.json();
        return result?.data ?? null;

      } catch (e) {
        if (
          e?.name === 'TypeError' ||
          e?.message?.includes('NetworkError') ||
          e?.message?.includes('Failed to fetch')
        ) {
          console.warn('Network error ignored:', e.message);
          return null;
        }

        console.error('Unexpected error:', e);
        return null;
      }
    },
    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file: ' + (reader.error?.message || 'unknown error')));
        reader.readAsDataURL(blob);
      });
    },
    async openFileMsgInfo([id, name, param]) {
      try {
        const base64Data = await this.getOriginFileInfo(id);
        if (!base64Data) {
          console.warn('File data unavailable for id:', id);
          return;
        }

        const arr = base64Data.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while(n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }

        const blob = new Blob([u8arr], { type: mime });
        const url = URL.createObjectURL(blob);

        if(param === 'download') {
          const link = document.createElement('a');
          link.href = url;
          link.download = name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          window.open(url, '_blank');
        }
      } catch (e) {
        console.error('Error opening file:', e);
      }
    },
    openImgMsgInfoOpen([id, isActiveMoodalSidebar]) {
      this.openImgMsgInfo(id)
      this.isActiveMoodalSidebar = isActiveMoodalSidebar;
    },
    async openImgMsgInfo(id) {
      if (!this.allChatImages) return;

      const index = this.allChatImages.findIndex(img => img.id === id);
      if (index === -1) return;
      
      this.activeImageIndex = index;
      this.isActiveModalImages = true;
      await this.preloadImageByIndex(index);

      await Promise.all([
        this.preloadImageByIndex(index - 1),
        this.preloadImageByIndex(index + 1),
      ]);
    },
    async preloadImageByIndex(index) {
      if (!this.allChatImages || index < 0 || index >= this.allChatImages.length) return;

      const img = this.allChatImages[index];
      if (!img || img.base64) return;

      const base64 = await this.getOriginFileInfo(img.id);

      if (!this.allChatImages || index >= this.allChatImages.length || !this.allChatImages[index]) return;

      this.$set
        ? this.$set(this.allChatImages, index, { ...img, base64 })
        : (this.allChatImages[index] = { ...img, base64 });
    },
    handleDragEnter(e) {
      if(this.updateMessageData) return;
      e.preventDefault();
      this.dragCounter++;
      this.isDraggingFiles = true;
    },
    handleDragLeave(e) {
      if(this.updateMessageData) return;
      e.preventDefault();
      this.dragCounter--;
      if (this.dragCounter <= 0) {
        this.isDraggingFiles = false;
        this.dragCounter = 0;
      }
    },
    handleDragOver(e) {
      if(this.updateMessageData) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    handleDrop(e) {
      if(this.updateMessageData) return;
      e.preventDefault();
      this.isDraggingFiles = false;
      this.dragCounter = 0;

      const files = Array.from(e.dataTransfer.files);
      if (files.length) {
        this.addFiles(files);
      }
    },
    handlePaste(e) {
      if (this.updateMessageData) return;
  
      const clipboardItems = e.clipboardData?.items;
      if (!clipboardItems) return;

      const files = [];

      for (const item of clipboardItems) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length) {
        e.preventDefault();
        this.addFiles(files);
      }
    },
    addFiles(files) {
      if(this.updateMessageData) return;
      this.draggingFiles = Array.from(files);
    },
    toggleSearch(bool) {
      this.isActiveSearchMsg = bool;
      if(bool) {
        this.isActiveTemplates = false;
      } else {
        this.activeTooltipBody = true;
        setTimeout(() => {
          this.activeTooltipBody = false;
        },1000)
      }
    },
    toggleUserInfo(_event = false, isClose = false) {
      this.isEditReplyMsg = false;
      this.isActiveTemplates = false;
      if(isClose) {
        this.isActiveUserInfo = false;
      } else {
        this.isActiveUserInfo = !this.isActiveUserInfo;
        this.$emit('toggle-user-info', this.isActiveUserInfo);
      }
      if(this.isTablet) {
        this.isActiveUserBG = this.isActiveUserInfo;
      } else {
        this.isActiveUserBG = false;
      }
    },
    userMute(id, isMute) {
      if (this.currentUser && id === this.currentUserUuid) {
          this.$emit('user-mute', {
            userId: id,
            isMute: isMute
          });
      }
    },
    handleDocumentClick(e) {
      const ref = this.$refs.chatContainer;
      const chatContainerEl = ref?.$el || ref;
      if (!chatContainerEl) return;

      const el = chatContainerEl.querySelector(`[data-id="${this.openTooltipMessageId}"]`);
      if (!el) return;
      el.style.transform = '';
      this.openTooltipMessageId = null;
    },
    handleTouchStart(event) {
      if(this.isTablet) {
        this.touchStartX = event.changedTouches[0].clientX;
        this.touchStartY = event.changedTouches[0].clientY;
      }
    },
    handleTouchEnd(e) {
      if(this.isTablet) {
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;

        const deltaX = touchEndX - this.touchStartX;
        const deltaY = touchEndY - this.touchStartY;

        if (Math.abs(deltaY) < 20 && deltaX > 80) {
          this.btnBack();
        }
      }
    },
    btnBack() {
      this.$emit('btn-back', 'body');
      this.isActiveTemplates = false;
    },
    userTemplate() {
      this.isActiveTemplates = !this.isActiveTemplates;
      if(this.isActiveTemplates) {
        this.isActiveSearchMsg = false;
      }
    },
    choseTemplate(text) {
      const chatInput = this.$refs.chatInput;
      if (!chatInput) return;
      chatInput.innerHTML = '';

      chatInput.textContent = this.decodeHtmlEntities(text);
      this.contentMsg = chatInput.textContent;
      this.inputPlaceholder = false;
    },
    addEmoji(emoji) {
      const chatInput = this.$refs.chatInput;
      if (!chatInput) return;

      let currentText = chatInput.innerText || '';

      chatInput.innerText = currentText + emoji;
      this.contentMsg = chatInput.innerText;
      this.inputPlaceholder = false;

      chatInput.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(chatInput);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    },
    handleReactionsMessage(socketData, ifAdd) {
      if (!socketData?.id) return;
      const messageId = socketData.id;

      for (const group of this.messageList) {
        const index = group.messages.findIndex(msg => msg.id === messageId);

       if (index !== -1) {
          const msg = group.messages[index];

          if (Array.isArray(socketData.reactions)) {
            msg.reactions = [...socketData.reactions];
          }
          return;
        }
      }
    },
    checkPlaceholder(evt) {
      this.contentMsg = evt.target.innerText;
      const el = evt.target;
      this.inputPlaceholder = el.innerText.trim() === '';
    },
    addNewFile(files) {
      if (!Array.isArray(files)) return;

      if (!Array.isArray(this.allChatImages)) {
        this.allChatImages = [];
      }

      if (!Array.isArray(this.allChatFiles)) {
        this.allChatFiles = [];
      }

      files.forEach(file => {
        if (file.preview_url) {
          if (!this.allChatImages.some(img => img.id === file.id)) {
            this.allChatImages.push(file);
          }
        } else {
          if (!this.allChatFiles.some(f => f.id === file.id)) {
            this.allChatFiles.push(file);
          }
        }
      });

      this.scrollToBottom();
    },
    updateText(text) {
      const chatInput = this.$refs.chatInput;
      if (!chatInput) return;
      // this.isEditReplyMsg = false;
      // this.updateMessageData = null;
      // this.replyEditMsgData = null;
      chatInput.innerHTML = '';

      chatInput.textContent = this.decodeHtmlEntities(text);
      this.contentMsg = chatInput.innerText;
      this.inputPlaceholder = chatInput.innerText.trim() === '';

      chatInput.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(chatInput);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    },
    clickBtnSend() {
      if (this.updateMessageData) {
        this.updateMessage();
      } else {
        this.sendMessage();
      }
    },
    handleKeyDown(event) {
      if (this.isMobile) return;

      if (event.key !== 'Enter') return;

      const isNotEmpty = event.target.innerText.trim() !== '';

      if (event.shiftKey) {
        document.execCommand('insertLineBreak');
        event.preventDefault();
        return;
      }

      if (!isNotEmpty) {
        event.preventDefault();
        return;
      }

      event.preventDefault();

      if (this.updateMessageData) {
        this.updateMessage();
      } else {
        this.sendMessage();
      }
    },
    handleFocus() {
      
    },
    generateTempId() {
      return Date.now() + Math.floor(Math.random() * 10000);
    },
    closeReply() {
      this.isEditReplyMsg = false;
      if (this.closeReplyTimeout) {
        clearTimeout(this.closeReplyTimeout);
      }
      this.closeReplyTimeout = setTimeout(() => {
        this.updateMessageData = null;
        this.replyEditMsgData = null;
        this.closeReplyTimeout = null;
        const el = this.$refs.chatInput;
        if (el) {
          el.innerText = '';
        }
        this.inputPlaceholder = true;
      }, 400);
    },
    replyMsg(msg) {
      this.handleDocumentClick()
      if (this.closeReplyTimeout) {
        clearTimeout(this.closeReplyTimeout);
        this.closeReplyTimeout = null;
      }
      const el = this.$refs.chatInput;
      this.isEditReplyMsg = true;
      this.replyEditMsgData = msg;
      this.updateMessageData = null;
      this.$nextTick(() => {
        if (!el) return;
        el.focus();

        const range = document.createRange();
        const selection = window.getSelection();

        range.selectNodeContents(el);
        range.collapse(false);

        selection.removeAllRanges();
        selection.addRange(range);
      });
    },
    onToggleTooltip(id) {
      const ref = this.$refs.chatContainer;
      const chatContainerEl = ref?.$el || ref;
      if (!chatContainerEl) return;
      //this.replyEditMsgData = null;
      const el = chatContainerEl.querySelector(`[data-id="${id}"]`);
      if (!el) return;

      const containerRect = chatContainerEl.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const distanceToTop = elRect.top - containerRect.top;
      const distanceToBottom = containerRect.bottom - elRect.bottom;
      const elHeight = elRect.height;
      const containerHeight = containerRect.height;
      let offset = 0;
      
      if (distanceToBottom < 240) {
        offset = -(240 - distanceToBottom);
      } else if (containerHeight - ((100 - distanceToTop) + elHeight) < 240 ) {
        offset = distanceToBottom - 240;
      } else if (distanceToTop < 100) {
        offset = 100 - distanceToTop;
      }
      
      el.style.transform = `translateY(${offset}px)`;
      if(this.isTablet) {
        this.openTooltipMessageId = id ? (this.openTooltipMessageId === id ? null : id) : null;
      } else {
        this.openTooltipMessageId = id;
      }
      if(this.openTooltipMessageId) {
        this.closeReply();
      }
    },
    findMessageById(id) {
      for (const group of this.messageList) {
        const msg = group.messages.find(m => m.id === id);
        if (msg) return msg;
      }
      return null;
    },
    reactionsEmoji(reactionData) {
      this.handleDocumentClick()

      // optimistic update: show the reaction right away, roll back if the request fails
      const msg = this.findMessageById(reactionData.messageId);
      const prevReactions = msg ? [...(msg.reactions || [])] : null;
      let optimisticReactions = null;
      if (msg) {
        optimisticReactions = prevReactions.filter(r => r.user_id !== this.myUser.id);
        if (!reactionData.removing) {
          optimisticReactions.push({ user_id: this.myUser.id, emoji: reactionData.emoji });
        }
        msg.reactions = optimisticReactions;
      }
      const rollback = () => {
        // skip if the socket already replaced the list with server data
        if (msg && toRaw(msg.reactions) === optimisticReactions) {
          msg.reactions = prevReactions;
        }
      };

      let url;
      let param;
      if(reactionData.removing) {
        url = '/chat-message-reaction/delete';
        param = {
          message_id: reactionData.messageId
        }
      } else {
        url = '/chat-message-reaction/create';
        param = {
          message_id: reactionData.messageId,
          emoji: reactionData.emoji
        }
      }
      axios.post(url, param)
      .then(res => {
        if (res?.data?.status && res.data.status !== 'success') rollback();
      })
      .catch(err => {
        console.error(err)
        rollback();
      });
    },
    openFindMessage(id) {
      this.searchMessageId = id;
      this.handleDocumentClick();
    
      if (this.highlightTimeout) {
        clearTimeout(this.highlightTimeout);
        this.highlightTimeout = null;
      }

      this.$nextTick(() => {
        const ref = this.$refs.chatContainer;
        const chatContainerEl = ref?.$el || ref;
        if (!chatContainerEl) return;

        const el = chatContainerEl.querySelector(`[data-id="${id}"]`);

        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });

          this.highlightTimeout = setTimeout(() => {
            this.searchMessageId = null;
            this.highlightTimeout = null;
          }, 3500);
        } else {
          this.getMessages(this.currentUserUuid, false, false, id)
            // .then(() => {
            //   this.$nextTick(() => this.openFindMessage(id));
            // });
        }
      });
    },
    decodeHtmlEntities(text) {
      if (!text) return '';

      const div = document.createElement('div')
      div.innerHTML = text

      return div.textContent || div.innerText || ''
    },
    deleteFilesFromChat(id) {
      this.allChatFiles = this.allChatFiles.filter(file => file.id !== id);
      this.allChatImages = this.allChatImages.filter(img => img.id !== id);
    },
    openUpdateMessage(msg) {
      this.handleDocumentClick()
      if (this.closeReplyTimeout) {
        clearTimeout(this.closeReplyTimeout);
        this.closeReplyTimeout = null;
      }
      this.updateMessageData = msg;
      const el = this.$refs.chatInput;
      this.isEditReplyMsg = true;
      this.replyEditMsgData = msg;
      if (!el) return;
      el.innerHTML = '';

      el.textContent = this.decodeHtmlEntities(msg.text);
      this.contentMsg = el.textContent;
      this.inputPlaceholder = false;
      this.$nextTick(() => {
        if (!el) return;
        el.focus();

        const range = document.createRange();
        const selection = window.getSelection();

        range.selectNodeContents(el);
        range.collapse(false);

        selection.removeAllRanges();
        selection.addRange(range);
      });
    },
    async updateMessage() {
      const chatInput = this.$refs.chatInput;
      if (!chatInput) return;
      const msg = chatInput.innerText.trim();

      if (((this.replyEditMsgData?.files?.length ?? 0) === 0 && !msg) || !this.updateMessageData) return;
      this.isEditReplyMsg = false;
      this.isActiveTemplates = false;
      const payload = {
        message_id: this.updateMessageData.id,
        conversation_uuid: this.currentUserUuid,
      };

      if (msg) {
        payload.text = msg;
      }

      if(this.updateMessageData.files.length) {
        const uploadFilesIds = Array.isArray(this.updateMessageData.files)
        ? this.updateMessageData.files
            .filter(f => Number.isInteger(Number(f?.id)))
            .map(f => Number(f.id))
        : [];

        payload.file_ids = uploadFilesIds;
      }
      
      chatInput.innerText = '';
      this.inputPlaceholder = true;
      this.contentMsg = '';

      try {
        const response = await axios.post(
          '/chat-api/update-message', payload,
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        this.closeReply();
      }
    },
    async sendMessage() {
      const chatInput = this.$refs.chatInput;
      if (this.isSending || !chatInput) return;

      const msg = chatInput.innerText.trim();
      if (!msg) return;

      this.isSending = true;

      const tempId = this.generateTempId();
      const newMessage = {
        timemark: tempId,
        id: tempId,
        type: 1,
        user_id: this.myUser.id,
        text: msg,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_read: false,
        just_sent: true,
        reactions: [],
        reply_to: this.isEditReplyMsg && this.replyEditMsgData ? {
          user_id: this.replyEditMsgData.user_id,
          text: this.replyEditMsgData.text,
        } : null
      };
      const replyToId = this.replyEditMsgData?.id ? this.replyEditMsgData.id : null;
      this.closeReply();
      this.responseMessageList.push(newMessage);
      this.groupMessagesByDate(this.responseMessageList);
      this.isEditReplyMsg = false;
      this.isActiveTemplates = false;

      chatInput.innerText = '';
      this.inputPlaceholder = true;
      this.isNoMessagesYet = false;
      this.contentMsg = '';
      try {
        const response = await axios.post(
          '/chat-api/add-message',
          {
            text: msg,
            conversation_uuid: this.currentUserUuid,
            reply_to_id: replyToId,
          },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );
        this.isSending = false;
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        this.isSending = false;
      }
    },
    handleScroll() {
      const chatContainerRef = this.$refs.chatContainer;
      if (!chatContainerRef || this.isLoadingMessages) return;

      const chatContainerEl = chatContainerRef.$el || chatContainerRef;
      const scrollTop = chatContainerEl.scrollTop;
      const scrollHeight = chatContainerEl.scrollHeight;
      const clientHeight = chatContainerEl.clientHeight;

      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      
      if (distanceFromBottom > 250) {
        this.isShowScrollBtn = true;
      } else {
        this.isShowScrollBtn = false;
      }

      if (scrollTop < 250 && this.isScrollNearTop) {
        this.getMessages(this.currentUserUuid, true, false);
        this.isScrollNearTop = false;
      } else if (scrollTop >= scrollHeight - 1000 && this.isScrollNearBottom) {
        this.getMessages(this.currentUserUuid, false, true);
        this.isScrollNearBottom = false;
      }
    },
    handleChatMessage(socketData, isUpdate) {
      if(socketData.conversation.uuid === this.currentUserUuid) {
        const { isBottom } = this.getScrollState();

        let newMessage
        if (socketData.message.type === 1) {
          this.visibleImgMessages.add(String(socketData.message.id));
          newMessage = {
            id: socketData.message.id,
            timemark: socketData.message.timemark,
            type: socketData.message.type,
            user_id: socketData.message.user_id ? socketData.message.user_id : this.currentUser.user.id,
            text: socketData.message.text,
            created_at: socketData.message.created_at,
            updated_at: socketData.message.created_at,
            is_read: false,
            reply_to: socketData.message.reply_to,
            is_updated: isUpdate,
            files: socketData.message.files,
          }
          this.addNewFile(socketData.message.files)
        } else {
          newMessage = {
            id: socketData.message.id,
            timemark: socketData.message.timemark,
            type: socketData.message.type,
            text: socketData.message.text,
            created_at: socketData.message.created_at,
            updated_at: socketData.message.created_at,
            metadata: socketData.message?.metadata
          }
        }

        if (isUpdate) {
          const updatedId = newMessage.id;

          this.responseMessageList.forEach((msg, i) => {
            if (msg.id === updatedId) {
              this.responseMessageList[i] = {
                ...msg,
                ...newMessage,
                is_updated: true
              };
            }

            if (msg.reply_to && msg.reply_to.id === updatedId) {
              this.responseMessageList[i].reply_to = {
                ...msg.reply_to,
                text: newMessage.text,
                is_updated: true
              };
            }
          });
        } else {
          if (this.myUser.id === socketData.message.user_id || socketData.message.type === 3) {
            const index = this.responseMessageList.findIndex(
                msg => msg.just_sent && msg.user_id === this.myUser.id
            );
            if (index !== -1) {
                this.responseMessageList.splice(index, 1, newMessage);
            } else {
              if(this.cacheAfter.length) {
                this.cacheAfter.push(newMessage);
              } else {
                this.responseMessageList.push(newMessage);
              }
            }
          } else {
              if(this.cacheAfter.length) {
                this.cacheAfter.push(newMessage);
              } else {
                this.responseMessageList.push(newMessage);
              }
          }

          if(socketData.message.type === 1) {
            //this.responseMessageList.forEach(msg => {
              //msg.is_read = true
            //})
          } else {
            const otherUserId = Number(
              Object.keys(socketData.conversation.users).find(id => Number(id) !== this.myUser.id)
            );
            if (this.$refs.userInfoRef) {
              this.$refs.userInfoRef.getOrderInfo(otherUserId);
            }
          }
        }
        
        if(this.cacheAfter.length === 0) {
          this.groupMessagesByDate(this.responseMessageList, false)
          if (isBottom) {
            this.scrollToBottom()
          }
        }
    
        this.isNoMessagesYet = false;
      }
    },
    markRead(uuid) {
      if (uuid === this.currentUserUuid) {
        this.messageList = this.messageList.map(conversation => ({
          ...conversation,
          messages: conversation.messages.map(message => ({
            ...message,
            is_read: true
          }))
        }));
      }
    },
    async getMessages(uuid, loadMoreBefore = false, loadMoreAfter = false, searchId = null) {
      if (this.isLoadingMessages) return;

      let messagesFromSource = null;

      if (loadMoreBefore && this.cacheBefore.length) {
        messagesFromSource = this.cacheBefore.splice(-this.responseSize);

        this.oldFirstTimemarkMsg = this.firstTimemarkMsg;
      }
      else if (loadMoreAfter && this.cacheAfter.length) {
        messagesFromSource = this.cacheAfter.slice(0, this.responseSize);
        this.cacheAfter = this.cacheAfter.slice(this.responseSize);

        this.oldLastTimemarkMsg = this.lastTimemarkMsg;
      }

      if (!messagesFromSource) {
        let url = `messages/all`;
        let params = {};

        if (loadMoreBefore) {
          params.starting_before = this.firstTimemarkMsg;
          params.conversation_uuid = uuid;
          this.oldFirstTimemarkMsg = this.firstTimemarkMsg;
          if (this.responseCountMsgBefore === 0) return;
        }
        else if (loadMoreAfter) {
          params.starting_after = this.lastTimemarkMsg;
          params.conversation_uuid = uuid;
          this.oldLastTimemarkMsg = this.lastTimemarkMsg;
          if (this.responseCountMsgAfter === 0) return;
        }
        else if (searchId) {
          url = `messages/around-message`;
          params.message_id = searchId;
        }
        else if (this.miniChat) {
          params.conversation_uuid = uuid;
          this.isMiniChatLoad = false;
        } else {
          params.conversation_uuid = uuid;
        }

        this.$emit('loading-messages', true);

        let requestUrl = `${this.testUrlQuickChat + url}`;
        let fetchOptions = {
          method: 'GET',
          headers: {}
        };

        if (searchId) {
          fetchOptions.method = 'POST';
          fetchOptions.headers['Content-Type'] = 'application/json';
          fetchOptions.body = JSON.stringify({
            message_id: searchId
          });
        } else {
          const query = new URLSearchParams(params).toString();
          requestUrl += `?${query}`;
        }

        try {
          const response = await chatFetch(requestUrl, fetchOptions);
          const result = await response.json();
          if (result.status !== 'success') return;
          messagesFromSource = result.data;
          if (loadMoreBefore) {
            this.responseCountMsgBefore = messagesFromSource.length;
          } else if (loadMoreAfter) {
            this.responseCountMsgAfter = messagesFromSource.length;
          } else if (searchId) {
            this.responseCountMsgBefore = messagesFromSource.length;
            this.responseCountMsgAfter = messagesFromSource.length;
          } else if (!this.miniChat) {
            this.lastIdMsgSearch = messagesFromSource[messagesFromSource.length - 1]?.id ?? null;
          }
          
          this.isMiniChatLoad = true;
        } catch (e) {
          console.error('Upload error:', e);
          return;
        }
        finally {
          this.$emit('loading-messages', false);
        }
      }

      if (loadMoreBefore) {
        let all = [
          ...messagesFromSource,
          ...this.responseMessageList
        ];

        const unique = [];
        const seen = new Set();
        for (const msg of all) {
          const key = msg.id || msg.timemark;
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(msg);
          }
        }

        this.responseMessageList = unique.slice(0, this.windowSize);
        this.cacheAfter = [...unique.slice(this.windowSize), ...this.cacheAfter];
      } else if (loadMoreAfter) {
        const all = [
          ...this.responseMessageList,
          ...messagesFromSource
        ];
        const unique = [];
        const seen = new Set();
        for (const msg of all) {
          const key = msg.id || msg.timemark;
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(msg);
          }
        }

        this.responseMessageList = unique.slice(-this.windowSize);
        const pushedToCacheBefore = unique.slice(0, unique.length - this.windowSize);

        this.cacheBefore = this.cacheBefore.concat(pushedToCacheBefore);
      } else {
        this.responseMessageList = messagesFromSource;
        this.cacheBefore = [];
        this.cacheAfter = [];

        const lastMessages = messagesFromSource.slice(-10);
        lastMessages.forEach(msg => {
          if (msg?.id) {
            this.visibleImgMessages.add(String(msg.id));
          }
        });
      }

      if (this.responseMessageList.length > 0) {
        this.isNoMessagesYet = false;
        this.isSkeletonMsgOn = false;

        this.firstTimemarkMsg = this.responseMessageList[0]?.timemark ?? null;
        this.lastTimemarkMsg = this.responseMessageList[this.responseMessageList.length - 1]?.timemark ?? null;

        this.groupMessagesByDate(
          this.responseMessageList,
          true,
          loadMoreBefore,
          loadMoreAfter,
          searchId
        );
      } else {
        this.isNoMessagesYet = true;
        this.isSkeletonMsgOn = false;
      }
    },
    isFirstInGroup(msg) {
      return this.firstInGroupIds.has(msg.timemark ? msg.timemark : msg.id);
    },

    groupMessagesByDate(messages, isScroll = true, loadMoreBefore = false, loadMoreAfter = false, searchId = null) {
      const groupedMessages = [];
      const today = new Date();
      const yesterday = new Date();
      const todayText = this.$t('Today');
      const yesterdayText = this.$t('Yesterday');
      const language = this.language
      yesterday.setDate(yesterday.getDate() - 1);
      
      function formatDate(timestamp, language, todayText, yesterdayText) {
          const date = new Date(timestamp);
          if (date.toDateString() === today.toDateString()) return todayText;
          if (date.toDateString() === yesterday.toDateString()) return yesterdayText;
          return date.toLocaleDateString(language, { month: 'short', day: '2-digit', year: 'numeric' });
      }
      
      const groupedByDate = {};
      const firstInGroupIds = new Set();
      let prevUserId = null;
      messages.forEach(message => {
          const messageDate = formatDate(
              new Date(message.created_at).getTime(),
              language,
              todayText,
              yesterdayText
          );

          if (!groupedByDate[messageDate]) {
              groupedByDate[messageDate] = [];
          }

          if (message.user_id !== prevUserId) {
              firstInGroupIds.add(message.timemark ? message.timemark : message.id);
          }

          prevUserId = message.user_id;

          groupedByDate[messageDate].push(message);
      });
      
      for (const date in groupedByDate) {
          groupedMessages.push({ date: date, messages: groupedByDate[date] });
      }

      this.firstInGroupIds = firstInGroupIds;
      this.messageList = groupedMessages

      this.$nextTick(() => {
        const ref = this.$refs.chatContainer;
        const chatContainerEl = ref?.$el || ref;
        if (!chatContainerEl) return;

        if (!!searchId) {
          const el = chatContainerEl.querySelector(`[data-id="${searchId}"]`);
          this.isScrollNearBottom = true;
          this.isScrollNearTop = true;
          if (el) {
            chatContainerEl.removeEventListener('scroll', this.handleScroll);
            const containerRect = chatContainerEl.getBoundingClientRect().top;
            const elRect = el.getBoundingClientRect().top;

            const offset = elRect - containerRect - chatContainerEl.clientHeight / 2 + el.clientHeight / 2;
            chatContainerEl.scrollTop = chatContainerEl.scrollTop + offset;
            chatContainerEl.addEventListener('scroll', this.handleScroll, { passive: true });
            setTimeout(() => {
              this.searchMessageId = null;
            }, 2500);
          }
        } else if (loadMoreAfter || loadMoreBefore) {
          this.isScrollNearBottom = true;
          this.isScrollNearTop = true;
          const el = chatContainerEl.querySelector(`[data-timemark="${loadMoreAfter ? this.oldLastTimemarkMsg : this.oldFirstTimemarkMsg}"]`);
          if (el) {
            chatContainerEl.removeEventListener('scroll', this.handleScroll);
            const offsetTop = el.getBoundingClientRect().top - chatContainerEl.getBoundingClientRect().top;
            chatContainerEl.scrollTop = chatContainerEl.scrollTop + offsetTop + (loadMoreAfter ? -200 : 100);
            chatContainerEl.addEventListener('scroll', this.handleScroll, { passive: true });
          }
        } else if (isScroll) {
          chatContainerEl.removeEventListener('scroll', this.handleScroll);
          chatContainerEl.scrollTop = chatContainerEl.scrollHeight - chatContainerEl.clientHeight;
          setTimeout(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                chatContainerEl.scrollTop = chatContainerEl.scrollHeight - chatContainerEl.clientHeight;
              });
            });

            chatContainerEl.addEventListener('scroll', this.handleScroll, { passive: true });
          }, 200);
        }
        
        this.initObserver();
      })
    },
    initObserver() {
      const rootEl = this.$refs.chatContainer?.$el;
      this.observer?.disconnect();
      this.observer = new IntersectionObserver(
        this.onIntersect,
        {
          root: rootEl,
          rootMargin: '500px',
          threshold: 0.01,
        }
      );

      this.observeMessages();
    },
    observeMessages() {
      this.$nextTick(() => {
        const nodes = this.$el.querySelectorAll('[data-msg-id]');
        nodes.forEach(el => this.observer?.observe(el));
      });
    },
    onIntersect(entries) {
      for (const entry of entries) {
        const id = entry.target.dataset.msgId;

        if (entry.isIntersecting) {
          this.visibleImgMessages.add(id);
        }
      }
    },
    handleDateHeaderClick(event) {
      const chatContainerEl = this.$refs.chatContainer;
      const parent = event.target.closest('.message-group');

      if (!chatContainerEl || !parent) return;

      const parentPositionTop = parent.offsetTop - 90;

      chatContainerEl.scrollTo({
        top: parentPositionTop,
        behavior: 'smooth',
      });
    },
    getScrollState() {
      const container = this.$refs.chatContainer?.$el || this.$refs.chatContainer
      if (!container) return { isBottom: true }

      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)

      return {
        isBottom: distanceFromBottom <= 5 
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer?.$el || this.$refs.chatContainer
        if (!container) return
        container.scrollTop = container.scrollHeight
      })
    },
    scrollToBottomBtn() {
      const ref = this.$refs.chatContainer;
      const chatContainerEl = ref?.$el || ref;
      if (!chatContainerEl) return;
      const el = chatContainerEl.querySelector(`[data-id="${this.lastIdMsgSearch}"]`);

      if (el) {
        chatContainerEl.removeEventListener('scroll', this.handleScroll);
        const offsetTop = el.getBoundingClientRect().top - chatContainerEl.getBoundingClientRect().top;
        const newScrollTop = chatContainerEl.scrollTop + offsetTop - 50;
        chatContainerEl.scrollTo({ top: newScrollTop, behavior: 'smooth' });
        chatContainerEl.addEventListener('scroll', this.handleScroll, { passive: true });
      } else {
        this.getMessages(this.currentUserUuid, false, false, this.lastIdMsgSearch)
      }
    },
    placeCursorAtEnd(el) {
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    },
    copyMessage(text) {
      this.handleDocumentClick()
      let cleanText = this.decodeHtmlEntities(text);
      let tempTextarea = document.createElement('textarea');
      tempTextarea.value = cleanText.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
      document.body.appendChild(tempTextarea);
      tempTextarea.select();
      document.execCommand('copy');
      document.body.removeChild(tempTextarea);
      renderAlertVue(this.$t('Text copied'), 'success', 2000)
    },
  }
};
</script>

<style lang="scss">
  @use '../style/ChatGroupMsg.scss' as *;
</style>

<style lang="scss" scoped>
  .template-btn-enter-active,
  .template-btn-leave-active {
      transition: 0.5s ease;
  }

  .template-btn-enter-from,
  .template-btn-leave-to {
      transform: translate(500%, 0);
      opacity: 0;
  }

  .template-btn-enter-to,
  .template-btn-leave-from {
      transform: translate(0);
      opacity: 1;
  }
  .chat-conversation{
    width: 100%;
    display: flex;
    gap: 1px;
    border-radius: 0 16px 16px 0;
    overflow: hidden;
    position: relative;
    transition: all .35s ease;
    &.mini{
      z-index: 3;
      transform: translateX(100%);
      //transition: 0.5s;
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      &.active{
        transform: translateX(0);
      }
    }
    @media only screen and (max-width: 767px) {
      border-radius: 0;
      z-index: 2;
      transform: translateX(100%);
      transition: 0.5s;
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      overflow: visible;
      &.active{
        transform: translateX(0);
        &::after{
          background: var(--wc-overlay);
          backdrop-filter: blur(4px);
          opacity: 1;
        }
      }
      &::after{
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        z-index: 1;
        pointer-events: none;
        background: var(--wc-overlay);
        backdrop-filter: blur(4px);
        transition: 0.5s;
        opacity: 0;
      }
    }
    
    .fade-info-bg-enter-active,
    .fade-info-bg-leave-active {
      transition: opacity 0.4s ease;
    }

    .fade-info-bg-enter-from,
    .fade-info-bg-leave-to {
      opacity: 0;
    }

    .fade-info-bg-enter-to,
    .fade-info-bg-leave-from {
      opacity: 1;
    }
    &__info-bg{
      position: absolute;
      z-index: 5;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      transition: 0.4s;
      background: var(--wc-backdrop);
    }
  }
  .chat-body{
      width: 100%;
      min-width: 0;
      z-index: 2;
      background-color: var(--wc-bg);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      position: relative;
      border-radius: 0 16px 16px 0;
      flex: 1 1 auto;
      min-width: 0;
      @media only screen and (max-width: 1023px) {
        z-index: 5;
        min-width: 100%;
      }
      &.mini{
        border-radius: 16px;
      }
      &__empty{
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        max-width: 260px;
        gap: 12px;
        z-index: 4;
        // Icon in a 48px tile, like the design empty states
        svg{
          width: 48px;
          height: 48px;
          padding: 12px;
          border-radius: 12px;
          background: var(--wc-surface);
          border: 1px solid var(--wc-border);
          box-sizing: border-box;
          path{
            fill: var(--wc-text-3);
          }
        }
        span{
          font-family: var(--wc-font-heading);
          font-weight: 400;
          font-size: 16px;
          line-height: 24px;
          color: var(--wc-text);
          text-align: center;
        }
        p{
          margin: -4px 0 0;
          font-family: var(--wc-font);
          font-size: 13px;
          line-height: 18px;
          color: var(--wc-text-2);
          text-align: center;
        }
      }
      &__wrap{
        height: 100%;
        overflow-y: hidden;
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        transition: 0.4s;
        &.mini{
          .chat-wrap{
            padding: 80px 20px 94px;
          }
        }
        // &.order-active{
        //   height: 586px;
        //   @media only screen and (max-width: 1640px) {
        //     height: 464px;
        //   }
        // }
        // Scrim over the whole thread while a message menu is open;
        // the open message (z-index 7) stays above it
        &:has(.message.hover)::before{
          content: '';
          position: absolute;
          inset: 0;
          z-index: 6;
          background: rgba(15, 23, 41, 0.06);
          pointer-events: none;
        }
        .chat-drop{
          position: absolute;
          inset: 12px;
          z-index: 100;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 24px;
          border: 2px dashed var(--wc-accent-2);
          border-radius: 16px;
          background: rgba(234, 241, 255, 0.94);
          text-align: center;
          pointer-events: none;
          &__icon{
            width: 56px;
            height: 56px;
            border-radius: 16px;
            background: var(--wc-surface);
            color: var(--wc-accent);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(43, 95, 217, 0.15);
          }
          &__title{
            font-family: var(--wc-font-heading);
            font-size: 16px;
            line-height: 24px;
            color: var(--wc-text);
          }
          &__hint{
            font-family: var(--wc-font);
            font-size: 13px;
            line-height: 18px;
            color: var(--wc-text-2);
          }
        }
        .chat-wrap{
          height: auto;
          padding: 0;
          position: relative;
          overflow-y: auto;
          scrollbar-gutter: stable;
          padding: 120px 60px 94px;
          min-height: 100%;
          display: flex;
          flex-direction: column;
          &.hide-scroll{
            overflow-y: hidden !important;
          }
          &.padding-top{
            padding-top: 64px;
          }
          &.edit-reply-msg{
            padding-bottom: 140px;
          }
          &__btn-to-bottom{
            position: absolute;
            bottom: 104px;
            right: 25px;
            border-radius: 100%;
            z-index: 100;
            background-color: var(--wc-bg);
            border: 1px solid var(--wc-border);
            box-shadow: 0px 4px 15px 0px var(--wc-shadow-strong);
            display: flex;
            align-items: center;
            justify-content: center;
            width: 35px;
            height: 35px;
            transition: 0.4s;
            &.active-info{
              transform: translateX(-320px);
            }
          }
          @media only screen and (max-width: 767px) {
            overflow-x: clip;
            padding-left: 20px;
            padding-right: 20px;
          }
          &.skeleton-wrap{
            justify-content: flex-end;
          }
          .msg-skeleton{
            width: 100%;
            max-width: 760px;
            margin: auto auto 0;
            display: flex;
            flex-direction: column;
            gap: 8px;
            &__row{
              display: flex;
              align-items: flex-end;
              gap: 8px;
              &.is-out{
                justify-content: flex-end;
              }
            }
            &__avatar{
              width: 28px;
              height: 28px;
              flex: none;
              border-radius: 50%;
              background: #EEF0F3;
            }
            &__avatar,
            &__bubble{
              animation: wc-skeleton-pulse 1.4s ease-in-out infinite;
            }
            &__bubble{
              display: block;
              max-width: 70%;
              border-radius: 12px;
              background: var(--wc-surface-3);
            }
            .is-out .msg-skeleton__bubble{
              background: #DCE6F7;
            }
          }
          @keyframes wc-skeleton-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.55; }
          }
          // &.search-active{
          //   .message-date{
          //     top: 60px;
          //   }
          // }
          // Thread column is capped like the design (760px) and centered
          .message-group{
            padding-top: 16px;
            position: relative;
            width: 100%;
            max-width: 760px;
            margin-left: auto;
            margin-right: auto;
            &:first-child{
              margin-top: auto;
            }
            // Details panel docked (>=1439px): re-center the column in the space left of it
            &.active-info{
              transform: translateX(-160px);
              transition: transform 0.6s ease;
            }
            &.orders-active{
              .message-date{
               top: 80px;
              }
            }
            &.one-system{
              top: -45%;
              .message-date{
                display: none;
              }
            }
          }
          .message-date{
            padding: 2px 10px;
            position: sticky;
            cursor: pointer;
            width: max-content;
            max-width: 160px;
            left: 50%;
            top: 12px;
            z-index: 6;
            transform: translateX(-50%);
            border-radius: 10px;
            background: var(--wc-surface-3);
            margin-bottom: 8px;
            isolation: isolate;
            transition: opacity 0.3s, transform 0.6s ease;
            user-select: none;
            &__block{
              font-family: var(--wc-font-medium);
              font-weight: 400;
              font-size: 12px;
              line-height: 16px;
              text-align: center;
              transition: 0.3s;
              color: var(--wc-text-2);
            }
            &.hidden {
              opacity: 0;
              pointer-events: none;
            }
          }
          .message{
            padding-bottom: 5px;
            // transition: 0.4s;
            position: relative;
            transition: 
              opacity 0.4s ease,
              transform 0.4s ease,
              z-index 0s linear 0.4s;
            &::before{
              content: "";
              position: absolute;
              top: 0;
              left: -70px;
              width: calc(100% + 140px);
              height: calc(100% - 5px);
              opacity: 0;
              transition: opacity 0.4s;
              z-index: 1;
              @media only screen and (max-width: 767px) {
                height: 100%;
                //top: -7px;
              }
            }
            &.support{
              display: flex;
              justify-content: center;
              &::before{
                height: calc(100% - 5px);
                //top: 5px;
              }
              &.last-system{
                :deep(.support-top__system) {
                  background: var(--wc-accent-soft);
                  color: var(--wc-accent);
                }
              }
              &.active-order{
                :deep(.support-top__system) {
                  color: var(--wc-accent);
                  // svg{
                  //   opacity: 1;
                  // }
                  background-size: 200% !important;
                  background: linear-gradient(90deg, var(--wc-skeleton-1) 0% 25%, var(--wc-skeleton-2) 50%, var(--wc-skeleton-1) 65% 100%);
                  animation: 2s wave-placeholder infinite linear !important;
                }
              }
            }
            &.opacity{
              //opacity: 0.3;
            }
            &.first{
              padding-top: 15px;
              &::before{
                top: 15px;
                height: calc(100% - 20px);
              }
              @media only screen and (max-width: 1023px) {
                padding-top: 5px;
                &::before{
                  top: 5px;
                  height: calc(100% - 10px);
                }
              }
            }
            &.hover{
              z-index: 7;
              transition:
              transform 0.4s ease,
              z-index 1s linear 0s;
              // dim + blur the rest of the thread while a message menu is open (as in difmark)
              &::before{ 
                background-color: var(--wc-focus-scrim);
                backdrop-filter: blur(2px);
                -webkit-backdrop-filter: blur(2px);
                top: -500px !important;
                height: 300vh !important;
                opacity: 1;
              }
            }
            &:last-child{
              padding: 0;
              &::before{
                height: 100%;
                top: 0;
                // @media only screen and (max-width: 767px) {
                //   height: calc(100% + 15px);
                //   top: -7px;
                // }
              }
              &.first{
                padding-top: 15px;
                &::before{
                  top: 15px;
                  height: calc(100% - 20px);
                }
                @media only screen and (max-width: 1023px) {
                  padding-top: 5px;
                  &::before{
                    top: 5px;
                    height: calc(100% - 10px);
                  }
                }
              }
              &.support{
                padding-top: 5px;
                &::before{
                  top: 5px;
                  height: calc(100% - 5px);
                  // @media only screen and (max-width: 1023px) {
                  //   top: 6px;
                  // }
                }
              }
            }
            &.active{
              &::before{
                opacity: 1;
                background: var(--wc-accent-soft);
              }
            }
            .message-wrap{
              display: flex;
              gap: 10px;
              &.author{
                flex-direction: row-reverse;
                .message-wrap__block-top{
                  justify-content: flex-end;
                }
                .message-wrap__block-body{
                  @media only screen and (max-width: 1023px) {
                    background-color: var(--wc-own);
                  }
                }
                .message-wrap__block-msg{
                  &::after{
                    background: var(--wc-surface);
                  }
                }
                .message-wrap__block-tooltip{
                  @media only screen and (max-width: 1023px) {
                    right: 0;
                    left: unset;
                    &::before{
                      left: unset;
                      right: 16px;
                    }
                  }
                }
                &.attention{
                  background: var(--wc-warning-soft);
                }
              }
              &.support{
                flex-direction: column;
                .support-top{
                  display: flex;
                  gap: 10px;
                  &__img{
                    width: 24px;
                    height: 24px;
                    position: relative;
                    img{
                      width: 100%;
                      height: 100%;
                      border-radius: 100%;
                      overflow: hidden;
                    }
                    .online-status{
                      position: absolute;
                      bottom: 1px;
                      right: 1px;
                      width: 7px;
                      height: 7px;
                      .ico-user-status{
                        display: block;
                        width: 100%;
                        height: 100%;
                      }
                    }
                  }
                  &__title{
                    font-family: 'Roboto-Medium';
                    font-weight: 400;
                    font-size: 16px;
                    line-height: 24px;
                    color: var(--wc-text);
                  }
                  &__system{
                    font-family: 'Roboto-Medium';
                    font-weight: 400;
                    font-size: 12px;
                    line-height: 120%;
                    color: var(--wc-accent);
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    background: var(--wc-accent-soft);
                    padding: 5px;
                    border-radius: 4px;
                    &.attention{
                      background: var(--wc-danger-soft);
                      color: var(--wc-danger);
                    }
                  }
                }
              }
              &.mini{
                .message-wrap{
                  &__block-body{
                    max-width: 253px;
                  }
                  &__block-top{
                    //opacity: 1;
                  }
                }
              }
              &__img{
                margin-top: 30px;
                width: 40px;
                min-width: 40px;
                height: 40px;
                border-radius: 100%;
                overflow: hidden;
                @media only screen and (max-width: 1023px) {
                  margin-top: 0;
                }
                &.skeleton-light{
                  max-width: 40px;
                  max-height: 40px;
                  border-radius: 100% !important;
                }
              }
              &__block{
                @media only screen and (max-width: 1023px) {
                  position: relative;
                  &-tooltip{
                    box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.25);
                    position: absolute;
                    width: 240px;
                    top: -70px;
                    left: 40px;
                    z-index: 10;
                    border-radius: 4px;
                    background-color: var(--wc-surface-2);
                    &::before{
                      content: "";
                      position: absolute;
                      left: 16px;
                      bottom: -6px;
                      width: 12px;
                      height: 12px;
                      background: var(--wc-surface-2);
                      -webkit-transform: rotateZ(45deg);
                      transform: rotateZ(45deg);
                      z-index: -1;
                    }
                    .tooltip-el{
                      display: flex;
                      padding: 15px 20px;
                      gap: 15px;
                      span{
                        color: var(--wc-text-2);
                        font-size: 16px;
                        line-height: 24px;
                        font-family: "Roboto-Regular";
                      }
                    }
                  }
                }
                &:hover{
                  .message-wrap__block-top{
                    opacity: 1;
                  }
                }
                &-top{
                  opacity: 0;
                  transition: 0.3s;
                  display: flex;
                  padding-bottom: 5px;
                  &.skeleton-top{
                    opacity: 1;
                  }
                  .copy{
                    width: 24px;
                    height: 24px;
                    border-radius: 8px;
                    background: var(--wc-surface);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                  }
                  .skeleton{
                    width: 24px !important;
                    height: 24px;
                    border-radius: 4px;
                  }
                }
                &-body{
                  background: var(--wc-surface);
                  border-radius: 5px;
                  padding: 10px;
                  max-width: 492px;
                  &.skeleton-body{
                    background: var(--wc-surface);
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                  }
                  @media only screen and (max-width: 1023px) {
                    background-color: var(--wc-surface-3);
                    &.hover{
                      background: var(--wc-own-border) !important;
                    }
                  }
                  @media only screen and (max-width: 767px) {
                    max-width: 263px;
                  }
                  &-time{
                    font-family: "Roboto-Regular", sans-serif;
                    font-weight: 400;
                    font-size: 12px;
                    line-height: 1.2;
                    color: var(--wc-text-3);
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    gap: 5px;
                    &.skeleton-light{
                      margin-top: 5px;
                      width: 35px !important;
                      height: 24px;
                      border-radius: 4px;
                    }
                    span{
                      &.readed{
                        svg{
                          path{
                            fill: var(--wc-accent);
                          }
                        }
                      }
                    }
                  }
                }
                &-msg{
                  font-family: "Roboto-Regular", sans-serif;
                  font-weight: 400;
                  font-size: 16px;
                  line-height: 1.5;
                  color: var(--wc-text);
                  padding-right: 5px;
                  margin-right: -5px;
                  overflow: hidden;
                  position: relative;
                  &::after{
                    //content: '';
                    position: absolute;
                    top: 0;
                    right: 0;
                    -webkit-mask-image: linear-gradient(90deg, transparent, black 9px 100%, transparent);
                    background: var(--wc-own);
                    width: 10px;
                    height: 100%;
                    @media only screen and (max-width: 1023px) {
                      background: var(--wc-surface-3);
                    }
                  }
                  :deep(a) { 
                    color: var(--wc-accent) !important; 
                    text-decoration: none;
                  }
                  a{
                    color: var(--wc-accent);
                  }
                  &.skeleton-light{
                    width: 157px !important;
                    height: 24px;
                    border-radius: 4px;
                  }
                }
              }
              
            }
          }
          &.mini{
            .message-group.one-system{
              top: -35%;
            }
            .message{
              &::before{
                left: -20px;
                width: calc(100% + 40px);
              }
            }
          }
        }
      }
      &__bottom{
        display: flex;
        flex-direction: column;
        z-index: 6;
        position: absolute;
        bottom: 0;
        left: 0;
        //position: relative;
        width: 100%;
        // Composer column matches the 760px thread column
        --wc-composer-x: max(24px, calc((100% - 760px) / 2));
        padding: 0 var(--wc-composer-x) 16px;
        background: linear-gradient(0deg, rgba(245, 247, 250, 0.95) 0%, rgba(245, 247, 250, 0) 100%);
        @media only screen and (max-width: 767px) {
          --wc-composer-x: 0px;
          padding: 0;
          background: var(--wc-surface);
          border-top: 1px solid var(--wc-border);
        }
        &.active-info{
          width: calc(100% - 320px);
        }
        &-wrap{
          display: flex;
          align-items: flex-end;
          gap: 2px;
          padding: 6px;
          position: relative;
          z-index: 2;
          background: var(--wc-surface);
          border: 1px solid var(--wc-border);
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(15, 23, 41, 0.06);
          transition: border-color 0.2s, box-shadow 0.2s;
          &:focus-within{
            border-color: var(--wc-accent-border);
          }
          @media only screen and (max-width: 767px) {
            border: 0;
            border-radius: 0;
            box-shadow: none;
            padding: 8px;
          }
        }
        // Reply / edit / templates: one card around the bar and the composer
        &::before{
          z-index: 1;
          position: absolute;
          left: var(--wc-composer-x);
          bottom: 16px;
          content: '';
          width: calc(100% - 2 * var(--wc-composer-x));
          height: calc(100% - 16px);
          background-color: var(--wc-surface);
          border: 1px solid var(--wc-border);
          box-shadow: 0 4px 16px rgba(15, 23, 41, 0.06);
          opacity: 0;
          transition: opacity 0.4s;
          border-radius: 12px;
          @media only screen and (max-width: 767px) {
            bottom: 0;
            height: 100%;
            border-radius: 0;
            box-shadow: none;
          }
        }
        &.active-back{
          &::before{
            opacity: 1;
          }
          .chat-body__bottom-wrap{
            background: transparent;
            border-color: transparent;
            box-shadow: none;
          }
        }
      }
      &__replyEdit{
        display: flex;
        position: relative;
        align-items: center;
        gap: 12px;
        border-radius: 12px 12px 0 0;
        padding: 8px 8px 8px 12px;
        border-bottom: 1px solid var(--wc-border);
        z-index: 2;
        > svg{
          width: 18px;
          height: 18px;
          min-width: 18px;
        }
        &-wrap{
          display: flex;
          align-items: center;
          justify-content: space-between;
          background-color: transparent;
          width: 100%;
          min-width: 0;
        }
        &-msg{
          max-width: calc(100% - 40px);
          overflow: hidden;
          padding: 0;
          display: flex;
          flex-direction: column;
          &-user{
            font-family: var(--wc-font-medium);
            font-weight: 400;
            font-size: 12px;
            line-height: 16px;
            color: var(--wc-accent);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
          }
          &-text{
            color: var(--wc-text-2);
            font-family: var(--wc-font);
            font-weight: 400;
            font-size: 13px;
            line-height: 18px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
        &-cancel{
          // 28px hit area around a 16px glyph (design: Composer edit bar)
          width: 28px;
          height: 28px;
          min-width: 28px;
          padding: 6px;
          box-sizing: border-box;
          color: var(--wc-text-3);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          &:hover{
            background: var(--wc-bg);
          }
          &:hover{
            color: var(--wc-text);
          }
        }
      }
      &__form{
        box-shadow: none;
        border-radius: 0;
        border: 0;
        z-index: 3;
        min-width: 0;
        height: auto;
        display: flex;
        justify-content: space-between;
        gap: 2px;
        align-items: flex-end;
        width: 100%;
        max-width: 100%;
        padding: 0;
        position: relative;
        background-color: transparent;
        transition: 0.4s;
        &-templates{
            display: flex;
            align-items: flex-end;
            z-index: 7;
            border-radius: 30px 30px 0 0;
            &-img{
                background-color: transparent;
                width: 32px;
                min-width: 32px;
                height: 32px;
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: 0.2s;
                @media only screen and (max-width: 1023px) {
                  width: 36px;
                  min-width: 36px;
                  height: 36px;
                }
                svg{
                  width: 18px;
                  height: 18px;
                }
                &:hover{
                    background-color: var(--wc-bg);
                }
                &.active{
                  background-color: var(--wc-accent-soft);
                  svg path{
                    stroke: var(--wc-accent);
                  }
                }
            }
        }
        &-input{
          width: 100%;
          max-width: 100%;
          position: relative;
          margin-bottom: 1px;
          min-width: 0;
          height: auto !important;
          flex-grow: 1;
          align-items: stretch;
          display: flex;
          flex-direction: column;
          .chat-input{
            touch-action: manipulation;
            width: 100%;
            font-family: var(--wc-font);
            font-weight: 400;
            overflow-y: auto;
            font-size: 14px;
            line-height: 20px;
            min-height: 32px;
            color: var(--wc-text);
            max-height: 150px;
            -webkit-user-modify: read-write-plaintext-only;
            user-modify: read-write-plaintext-only;
            padding: 6px 4px;
            outline: none;
            white-space: pre-wrap;
            word-break: break-word;
            @media only screen and (max-width: 767px) {
              font-size: 16px;
              padding: 8px 4px;
            }
            &__placeholder{
              position: absolute;
              left: 4px;
              top: 50%;
              transform: translateY(-50%);
              font-family: var(--wc-font);
              font-weight: 400;
              font-size: 14px;
              line-height: 20px;
              color: var(--wc-text-3);
              pointer-events: none;
              white-space: nowrap;
            }
          }
        }
      }
      .skeleton{
          background: linear-gradient(
              90deg,
              var(--wc-skeleton-1) 0% 25%,
              var(--wc-skeleton-2) 50%,
              var(--wc-skeleton-1) 65% 100%
          );
          border-radius: 4px;
          width: 100%;
          display: block;
          animation: 1s wave-placeholder infinite linear !important;
          background-size: 200% !important;
      }
      .skeleton-light{
          background: linear-gradient(
              90deg,
              var(--wc-skeleton-1) 0% 25%,
              var(--wc-skeleton-1) 50%,
              var(--wc-skeleton-1) 65% 100%
          );
          border-radius: 4px;
          width: 100%;
          display: block;
          animation: 1s wave-placeholder infinite linear !important;
          background-size: 200% !important;
      }
      @keyframes wave-placeholder {
          from {
              background-position: 100% 0%;
          }
          to {
              background-position: -100% 0%;
          }
      }
  }

  .message.just-sent {
      transform-origin: bottom;
      overflow: hidden;
      animation: slideInFromBottom 0.4s ease-out;
  }

  @keyframes slideInFromBottom {
      from {
          transform: translateY(100%);
          opacity: 0;
      }
      to {
          transform: translateY(0);
          opacity: 1;
      }
  }
</style>